import { getOperation, listAllOperationItems, listOperations } from '@/lib/api/client/operations'
import type { Operation, OperationItem, OperationListScope } from '@/lib/api/schemas/operations.schema'
import { isActiveOperation } from '@/lib/operations/labels'

export const ACTIVE_POLL_MS = 2_000
export const MAX_ACTIVE_POLL_MS = 10_000
export const IDLE_POLL_MS = 15_000
export const OPERATION_LIST_LIMIT = 50

export type ListSnapshot = { operations: Operation[]; hasMore: boolean; failed: boolean }
export type ItemsSnapshot = { operationId: string; items: OperationItem[] | null; failed: boolean }
export type WatchSnapshot = { operation: Operation | null; failed: boolean }

type Listener<T> = (snapshot: T) => void
type Channel<T> = { listeners: Set<Listener<T>>; snapshot: T | null }

type Environment = {
  document: Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'> | null
  setTimeout: (callback: () => void, ms: number) => number
  clearTimeout: (handle: number) => void
}

function browserEnvironment(): Environment {
  return {
    document: typeof document === 'undefined' ? null : document,
    setTimeout: (callback, ms) => window.setTimeout(callback, ms),
    clearTimeout: (handle) => window.clearTimeout(handle),
  }
}

/**
 * The one operations poller of a browser tab. The bubble, the Операции page and any
 * dialog subscribe here, so a tab sends one list request per scope per tick however
 * many views watch it. Ticks run every 2 s after a change, back off to 10 s while
 * nothing changes, slow to 15 s when nothing is queued or running, and stop while
 * the tab is hidden.
 */
export class OperationsPoller {
  private readonly lists = new Map<OperationListScope, Channel<ListSnapshot>>()
  private readonly items = new Map<string, Channel<ItemsSnapshot>>()
  private readonly watches = new Map<string, Channel<WatchSnapshot>>()
  private readonly environment: Environment
  private timer: number | null = null
  private controller: AbortController | null = null
  private inFlight = false
  private again = false
  private interval = ACTIVE_POLL_MS
  private signature: string | null = null
  private listening = false

  constructor(environment: Environment = browserEnvironment()) {
    this.environment = environment
  }

  subscribeList(scope: OperationListScope, listener: Listener<ListSnapshot>): () => void {
    return this.join(this.lists, scope, listener)
  }

  subscribeItems(operationId: string, listener: Listener<ItemsSnapshot>): () => void {
    return this.join(this.items, operationId, listener)
  }

  watchOperation(operationId: string, listener: Listener<WatchSnapshot>): () => void {
    return this.join(this.watches, operationId, listener)
  }

  /** Poll now and return to the fastest cadence, as after an enqueue or a cancel. */
  refresh(): void {
    this.interval = ACTIVE_POLL_MS
    this.signature = null
    if (!this.hasSubscribers()) return
    this.clearTimer()
    if (this.inFlight) {
      this.again = true
      return
    }
    void this.tick()
  }

  get currentInterval(): number {
    return this.interval
  }

  private join<T>(channels: Map<string, Channel<T>>, key: string, listener: Listener<T>): () => void {
    const existing = channels.get(key)
    const channel = existing ?? { listeners: new Set<Listener<T>>(), snapshot: null }
    channels.set(key, channel)
    channel.listeners.add(listener)
    if (channel.snapshot !== null) listener(channel.snapshot)
    this.listen()
    if (existing === undefined) this.refresh()
    return () => {
      channel.listeners.delete(listener)
      if (channel.listeners.size === 0) channels.delete(key)
      if (!this.hasSubscribers()) this.stop()
    }
  }

  private hasSubscribers(): boolean {
    return this.lists.size + this.items.size + this.watches.size > 0
  }

  private hidden(): boolean {
    return this.environment.document?.visibilityState === 'hidden'
  }

  private readonly onVisibilityChange = (): void => {
    if (this.hidden()) {
      this.clearTimer()
      this.controller?.abort()
      return
    }
    this.refresh()
  }

  private listen(): void {
    if (this.listening || this.environment.document === null) return
    this.environment.document.addEventListener('visibilitychange', this.onVisibilityChange)
    this.listening = true
  }

  private stop(): void {
    this.clearTimer()
    this.controller?.abort()
    this.controller = null
    this.again = false
    this.signature = null
    this.interval = ACTIVE_POLL_MS
    if (this.listening && this.environment.document !== null) {
      this.environment.document.removeEventListener('visibilitychange', this.onVisibilityChange)
    }
    this.listening = false
  }

  private clearTimer(): void {
    if (this.timer === null) return
    this.environment.clearTimeout(this.timer)
    this.timer = null
  }

  private anyActive(): boolean {
    for (const channel of this.lists.values()) {
      if (channel.snapshot?.operations.some(isActiveOperation)) return true
    }
    for (const channel of this.watches.values()) {
      const operation = channel.snapshot?.operation
      if (operation === null || operation === undefined || isActiveOperation(operation)) return true
    }
    return false
  }

  private itemsStillMove(operationId: string, snapshot: ItemsSnapshot | null): boolean {
    if (snapshot === null || snapshot.items === null || snapshot.failed) return true
    for (const channel of this.lists.values()) {
      const operation = channel.snapshot?.operations.find((candidate) => candidate.id === operationId)
      if (operation !== undefined) return isActiveOperation(operation)
    }
    return true
  }

  private async tick(): Promise<void> {
    if (this.hidden() || !this.hasSubscribers()) return
    this.inFlight = true
    const controller = new AbortController()
    this.controller = controller
    const { signal } = controller
    const lists = [...this.lists.keys()]
    const itemIds = [...this.items.entries()]
      .filter(([operationId, channel]) => this.itemsStillMove(operationId, channel.snapshot))
      .map(([operationId]) => operationId)
    const watchIds = [...this.watches.keys()]
    const [listResults, itemResults, watchResults] = await Promise.all([
      Promise.allSettled(lists.map((scope) => listOperations({ state: scope, limit: OPERATION_LIST_LIMIT }, signal))),
      Promise.allSettled(itemIds.map((operationId) => listAllOperationItems(operationId, signal))),
      Promise.allSettled(watchIds.map((operationId) => getOperation(operationId, signal))),
    ])
    this.inFlight = false
    if (this.controller === controller) this.controller = null
    if (signal.aborted) {
      if (this.again) {
        this.again = false
        void this.tick()
      }
      return
    }
    lists.forEach((scope, index) => {
      const result = listResults[index]
      const channel = this.lists.get(scope)
      if (channel === undefined || result === undefined) return
      channel.snapshot = result.status === 'fulfilled'
        ? { operations: result.value.items, hasMore: result.value.nextCursor !== null, failed: false }
        : { operations: channel.snapshot?.operations ?? [], hasMore: channel.snapshot?.hasMore ?? false, failed: true }
    })
    itemIds.forEach((operationId, index) => {
      const result = itemResults[index]
      const channel = this.items.get(operationId)
      if (channel === undefined || result === undefined) return
      channel.snapshot = result.status === 'fulfilled'
        ? { operationId, items: result.value, failed: false }
        : { operationId, items: channel.snapshot?.items ?? null, failed: true }
    })
    watchIds.forEach((operationId, index) => {
      const result = watchResults[index]
      const channel = this.watches.get(operationId)
      if (channel === undefined || result === undefined) return
      channel.snapshot = result.status === 'fulfilled'
        ? { operation: result.value, failed: false }
        : { operation: channel.snapshot?.operation ?? null, failed: true }
    })
    this.pace()
    this.publish()
    if (this.again) {
      this.again = false
      void this.tick()
      return
    }
    this.schedule()
  }

  private pace(): void {
    const signature = JSON.stringify({
      lists: [...this.lists.entries()].map(([scope, channel]) => [scope, channel.snapshot]),
      items: [...this.items.entries()].map(([operationId, channel]) => [operationId, channel.snapshot]),
      watches: [...this.watches.entries()].map(([operationId, channel]) => [operationId, channel.snapshot]),
    })
    const changed = signature !== this.signature
    this.signature = signature
    if (!this.anyActive()) {
      this.interval = changed && this.anyFailed() ? ACTIVE_POLL_MS : IDLE_POLL_MS
      return
    }
    this.interval = changed ? ACTIVE_POLL_MS : Math.min(this.interval * 2, MAX_ACTIVE_POLL_MS)
  }

  private anyFailed(): boolean {
    for (const channel of this.lists.values()) if (channel.snapshot?.failed) return true
    for (const channel of this.items.values()) if (channel.snapshot?.failed) return true
    for (const channel of this.watches.values()) if (channel.snapshot?.failed) return true
    return false
  }

  private publish(): void {
    for (const channel of [...this.lists.values()]) {
      const snapshot = channel.snapshot
      if (snapshot !== null) for (const listener of [...channel.listeners]) listener(snapshot)
    }
    for (const channel of [...this.items.values()]) {
      const snapshot = channel.snapshot
      if (snapshot !== null) for (const listener of [...channel.listeners]) listener(snapshot)
    }
    for (const channel of [...this.watches.values()]) {
      const snapshot = channel.snapshot
      if (snapshot !== null) for (const listener of [...channel.listeners]) listener(snapshot)
    }
  }

  private schedule(): void {
    this.clearTimer()
    if (this.hidden() || !this.hasSubscribers()) return
    this.timer = this.environment.setTimeout(() => {
      this.timer = null
      void this.tick()
    }, this.interval)
  }
}

export const operationsPoller = new OperationsPoller()
